<template>
  <div :class="getItemTableContainerClass">
    <ItemTable
      ref="itemTable"
      :collectionFilters="collectionFilters"
      :columns="columns"
      :details="true"
      :gettingStartedItemType="itemType"
      :handleBulkDelete="handleBulkDelete"
      :handleDetailsButtonClick="handleDetailsButtonClick"
      :handleMainAction="handleMainAction"
      :handleRowClick="handleRowClick"
      :hasSelectColumn="true"
      :isAsideOpen="isAsideOpen"
      :itemType="getItemTableType"
      :keepFresh="true"
      :linkText="groupLinkText"
      :mainActionMenuItems="mainActionMenuItems"
      :reportType="reportType"
      :route="route"
      :selectedModelId="selectedModelId"
      :selectionItemText="groupItemText"
      :setGettingStartedTooltipVisibility="setGettingStartedTooltipVisibility"
      :showBound="false"
      :showBulkDelete="true"
      :showMainAction="true"
      :showManagePolicyGroupTemplates="showManagePolicyGroupTemplates"
      :useDropDownFilterComponent="dropDownFilterComponent"
    />
    <SeverityModal
      v-if="showDeleteUserGroupModal"
      :isMarkdown="true"
      :notificationMessage="deleteUserGroupNotificationMessage"
      :notificationTitle="severityModalData.notificationTitle"
      :numberAffectedProp="selectedItems.length"
      :primaryButtonProps="severityModalData.primaryButtonProps"
      :showModal="showDeleteUserGroupModal"
      :title="severityModalData.title"
      useInputNumber
      variant="sev3"
      @close="handleCancelDeleteUserGroupModal"
      @save="continueBulkDelete"
    >
      <template v-slot:subtitle>
        <span>{{ severityModalData.subtitle }}</span>
      </template>
      <template v-slot:learnmore>
        <ParagraphBody>
          <span>For more information, see </span>
          <Link
            :href="severityModalData.kbLink"
            :style="severityModalData.kbLinkStyle"
            target="_blank"
            :text="severityModalData.kbText"
          />
        </ParagraphBody>
      </template>
      <template v-slot:list>
        <ParagraphBody>
          {{ severityModalData.numConfirmText }}
        </ParagraphBody>
      </template>
    </SeverityModal>
    <Modal
      v-model="modalData.visible"
      cancelText="cancel"
      :handleMainAction="modalData.handleMainAction"
      :headerText="modalData.headerText"
      :mainActionAttention="modalData.mainActionAttention"
      :mainActionText="modalData.mainActionText"
    >
      <p>{{ modalData.bodyText }}</p>
    </Modal>
    <ProgressModal
      storeModule="ProgressModule"
    />
    <AlertContainer>
      <DeletedItemsNotifications
        :itemType="getItemTableType"
      />
      <Alert
        v-if="show403error"
        :delay="7000"
        type="error"
        @hidden="handle403AlertHidden"
      >
        <span>{{ errorMessageFor403 }}</span>
      </Alert>
      <Alert
        v-if="defaultDeviceGroupFailedTasks.length > 0"
        :delay="7000"
        :interactive="true"
        type="error"
        @hidden="handleDefaultGroupAlertHidden"
      >
        <span>
          {{ genericDefaultDeviceGroupErrorMessage }}
        </span>
      </Alert>
    </AlertContainer>
  </div>
</template>

<script>
import {
  DefaultDeviceGroupErrorMessages,
  DirectoryGroupingConsts,
  FilterComponentConsts,
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeDisplayNames,
  ItemTypeRoutes,
  LocalStorageConsts,
  StoreModuleMap,
} from 'client/util/Constants';
import {
  Link,
  ParagraphBody,
  SeverityModal,
} from '@jumpcloud/ui-components';
import { ReportTypes } from 'client/Reports/ReportMetadata';
import { eventLocations, eventProperties, events } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { extend, find, get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Alert from 'client/components/Alert/Alert.vue';
import AlertContainer from 'client/components/Alert/AlertContainer.vue';
import DeletedItemsNotifications from 'client/components/ItemTable/DeleteItemsNotifications.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import EventManager from 'client/GrowthEngineering/EventManager';
import GroupItemTableColumns from 'client/components/ItemTable/GroupItemTableColumns';
import GroupTypeIcon from 'client/components/ItemTable/BodyComponents/GroupTypeIcon.vue';
import Groupfilters from 'client/components/ItemTable/CollectionFilterComponents/constants/GroupFilters';
import ItemTable from 'client/components/ItemTable/ItemTable.vue';
import ItemTableComputedMixin from 'client/components/ItemTable/mixins/ItemTableComputedMixin';
import ItemTableMethodsMixin from 'client/components/ItemTable/mixins/ItemTableMethodsMixin';
import ItemTablePropsMixin from 'client/components/ItemTable/mixins/ItemTablePropsMixin';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import LocalStorageService from 'client/util/LocalStorageService';
import MembershipUpdates from 'client/components/ItemTable/BodyComponents/MembershipUpdates';
import Modal from 'client/components/Modal/Modal.vue';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import MspState from 'client/components/models/MspState';
import PageNavigationUtil from 'client/util/PageNavigationUtil';
import ProgressModal from 'client/components/Modal/ProgressModal.vue';
import RouterService from 'client/util/RouterService';
import pluralize from 'pluralize';
import rbacUtil from 'client/util/rbacUtils';

const { columnTypes } = ItemTableConsts;
const {
  configuration, configurationGroup, device, deviceGroup,
} = ItemTypeConsts;
const { policyGroup, systemGroup, userGroup } = DirectoryGroupingConsts.groupTypes;
const { policy, system, user } = DirectoryGroupingConsts.memberTypes;
const {
  defaultIosADEGroupFailureMsg,
  defaultIosUserEnrollmentGroupFailureMsg,
  defaultMacOSADEGroupFailureMsg,
} = DefaultDeviceGroupErrorMessages;

const {
  description,
  location,
  section,
  text,
} = eventProperties;

const {
  policyGroupsPage,
} = eventLocations;

let columns = [];
const parseErrorMessages = (failedTasks) => {
  const genericFailures = [];
  const defaultDeviceGroupFailedTasks = [];
  const failedIds = [];
  const adeErrors = [
    defaultIosADEGroupFailureMsg,
    defaultMacOSADEGroupFailureMsg,
    defaultIosUserEnrollmentGroupFailureMsg,
  ];

  failedTasks.forEach((failed) => {
    const { id, error } = failed;
    failedIds.push(id);
    const hasAdeError = adeErrors.some((adeError) => error.message.includes(adeError));

    if ((error instanceof Error) && hasAdeError) {
      defaultDeviceGroupFailedTasks.push({ id, error });
    } else {
      genericFailures.push({ id, error });
    }
  });

  return {
    parsedFailedTasks: genericFailures,
    defaultDeviceGroupFailedTasks,
    failedIds,
  };
};

export default {
  name: 'GroupItemTable',

  components: {
    Alert,
    AlertContainer,
    DeletedItemsNotifications,
    ItemTable,
    Link,
    Modal,
    ParagraphBody,
    ProgressModal,
    SeverityModal,
  },

  EventHelper,

  mixins: [
    ItemTableComputedMixin,
    ItemTableMethodsMixin,
    ItemTablePropsMixin,
  ],

  props: {
    handleColumnAction: {
      type: Function,
      default: () => {},
    },
    itemType: {
      type: String,
      default: ItemTypeConsts.group,
    },
  },

  data() {
    const displayName = ItemTypeDisplayNames[ItemTypeConsts.group];

    columns = [
      {
        classNames: {
          body: [columnTypes.iconColumn],
          heading: [columnTypes.iconColumn],
        },
        bodyComponent: 'GroupTypeIcon',
        bodyComponentProps: {
          itemType: ItemTypeConsts.group,
        },
        headingText: 'Type',
        sortBy: 'type',
      },
      {
        bodyComponent: 'ItemName',
        bodyComponentProps: {
          titleField: 'name',
          subtitleFunction: this.subtitleFunction,
        },
        headingText: 'Group',
        sortBy: 'name',
      },
    ];

    return {
      collectionFilters: [],
      columns,
      displayName,
      defaultDeviceGroupFailedTasks: [],
      groupItemText: {
        label: 'selected',
        plural: pluralize(displayName),
        singular: displayName,
      },
      groupLinkText: 'Groups',
      itemTableType: ItemTypeConsts.group,
      mainActionMenuItems: [
        {
          component: GroupTypeIcon,
          componentProps: {
            item: { type: userGroup },
            smallIcon: true,
          },
          href: '#/groups/user/new',
          iconClassName: 'jc-groups-users',
          text: 'Create Group of Users',
        },
        {
          component: GroupTypeIcon,
          componentProps: {
            item: { type: systemGroup },
            smallIcon: true,
          },
          href: '#/groups/device/new',
          iconClassName: 'jc-groups-systems',
          text: 'Create Group of Systems',
        },
      ],
      severityModalData: {
        primaryButtonProps: {
          isDisabled: false,
          isLoading: false,
          hasOutline: true,
          text: 'Delete User Group',
          type: 'error',
        },
        kbText: '',
        kbLink: '',
        kbLinkStyle: { display: 'contents' },
        notificationTitle: '',
        notificationMessage: '',
        numConfirmText: '',
        subtitle: '',
        title: '',
      },
      modalData: {
        bodyText: '',
        handleMainAction: null,
        headerText: '',
        mainActionAttention: false,
        mainActionText: '',
        visible: false,
      },
      panelType: ItemTableConsts.panelType.mainPanel,
      reportType: this.itemType === ItemTypeConsts.userGroup ? ReportTypes.userToUserGroup : null,
      route: ItemTypeRoutes[ItemTypeConsts.group],
      show403error: false,
      showDeleteUserGroupModal: false,
      showManagePolicyGroupTemplates: false,
      dropDownFilterComponent: null,
      errorMessageFor403: rbacUtil.getForbiddenMessage(),
    };
  },

  computed: {
    ...mapGetters('SettingsModule', [
      'isAdminWithBilling',
      'isAdmin',
      'isManager',
    ]),

    deleteUserGroupNotificationMessage() {
      return `Deletion will permanently remove all user group data and associations. This action cannot be undone.

    • This will result in the removal of all user assocations made by that user group, including associations with devices and applications.

    • Users removed from devices as a result will no longer be able to log in to their JumpCloud-managed devices.

    • You are about to permanently delete **${this.selectedItems.length}** user group(s).
    `;
    },

    genericDefaultDeviceGroupErrorMessage() {
      const numOfGroups = this.defaultDeviceGroupFailedTasks.length;
      const reason = numOfGroups === 1 ? 'it is a default group' : 'they are default groups';
      return `${numOfGroups} ${pluralize('Group', numOfGroups)} ${pluralize('was', numOfGroups)} unable to be removed from JumpCloud because ${reason}. Change the default and try again.`;
    },

    getItemTableType() {
      return this.itemType === ItemTypeConsts.userGroup
        ? ItemTypeConsts.group : this.itemType;
    },
  },

  // The beforeCreate hook is used to load the specific store for each type of group,
  // which fixes a race condition when moving from one main group page to another,
  // specifically from the user group page to devices groups or policy groups page.
  // @see: https://jumpcloud.atlassian.net/browse/UN-675
  // The user group panel uses the default group collection due to specific logic
  // implemented there for this group type.
  beforeCreate() {
    const { itemType } = this.$options.propsData;
    const storeModule = itemType === ItemTypeConsts.userGroup
      ? StoreModuleMap[ItemTypeConsts.group] : StoreModuleMap[itemType];

    if (storeModule) {
      extend(
        this.$options.computed,
        mapGetters(storeModule, {
          items: 'getCollection',
          groupsIneligibleForDeletion: 'getGroupsIneligibleForDeletion',
          selectedItems: 'getSelectedItems',
        }),
      );
      extend(
        this.$options.methods,
        mapActions(storeModule, [
          'deleteItems',
          'unselectItems',
          'setDeletedItemIds',
          'resetDeletedItemIds',
        ]),
      );
    }
  },

  beforeMount() {
    this.updateData();
  },

  mounted() {
    if (this.itemType === ItemTypeConsts.deviceGroup) {
      columns.push(GroupItemTableColumns.defaultDeviceGroup);
    }

    if (this.itemType === ItemTypeConsts.userGroup) {
      this.dropDownFilterComponent = FilterComponentConsts.GroupDropDownFilter;
      columns.push(
        GroupItemTableColumns.administratorSudo,
        GroupItemTableColumns.passwordlessSudo,
      );
      columns.push({
        bodyComponent: MembershipUpdates,
        bodyComponentProps: {
          displayModal: this.handleColumnAction,
          review: true,
        },
      });
    }

    const canManage = this.isAdminWithBilling || this.isAdmin || this.isManager;

    if (this.itemType === ItemTypeConsts.configurationGroup
    && !!this.$hasFeatureFlag(this.$featureFlags.policyGroupTemplateGallery)
    && canManage) {
      this.mainActionMenuItems = [
        {
          text: 'Policy Group',
          clickHandler: this.handleOpenNewConfigurationGroupClick,
        },
        {
          text: 'Policy Group From Template',
          clickHandler: this.handleOpenNewPolicyGroupTemplateClick,
        },
      ];
    }
    if (this.itemType === ItemTypeConsts.configurationGroup
      && !!this.$hasFeatureFlag(this.$featureFlags.policyGroupTemplateManage)
      && MspState.isAnMsp
      && canManage) {
      this.showManagePolicyGroupTemplates = true;
    }
  },

  methods: {
    async continueBulkDelete() {
      ModalEventBus.close();
      this.modalData.visible = false;

      const isUserGroup = this.itemType === ItemTypeConsts.userGroup;
      if (isUserGroup) {
        const { adGroups } = this.groupsIneligibleForDeletion;

        if (adGroups.length > 0) {
          this.promptUndeletableGroupModal();
        }

        // Remove AD managed groups from selection
        const ids = this.groupsIneligibleForDeletion.adGroups.map(adGroup => adGroup.id);
        this.unselectItems({ ids });

        // Set RBAC callback in case of 403 error
        rbacUtil.dontUseNotifyUtil(() => {
          this.show403error = true;
        });
      }

      // Delete selected groups
      const response = await this.deleteItems(this.selectedItems);
      const { deletedItemIds = [], deletedIds = [], failedTasks = [] } = response;
      const {
        parsedFailedTasks = [],
        defaultDeviceGroupFailedTasks = [],
        failedIds = [],
      } = parseErrorMessages(failedTasks);
      this.defaultDeviceGroupFailedTasks = defaultDeviceGroupFailedTasks;
      this.failedIds = failedIds;

      if (failedIds.length > 0) {
        this.unselectItems({ ids: failedIds });
      }
      this.setDeletedItemIds({
        deletedItemIds: isUserGroup ? deletedItemIds : deletedIds,
        failedTasks: parsedFailedTasks,
      });

      this.$emit('policyGroupsDeleted');
    },

    handle403AlertHidden() {
      this.show403error = false;
    },

    handleBulkDelete() {
      this.closeAsidePanel();
      this.showDeleteModal();
    },

    handleCancelDeleteUserGroupModal() {
      this.showDeleteUserGroupModal = false;
    },

    handleDefaultGroupAlertHidden() {
      this.defaultDeviceGroupFailedTasks = [];
      this.resetDeletedItemIds('defaultDeviceGroupFailedTasks');
    },

    handleDetailsButtonClick(itemId) {
      const item = find(this.items, i => i.id === itemId);
      const urlType = {
        [systemGroup]: system,
        [userGroup]: user,
        [policyGroup]: policy,
      };
      let url;

      if (this.itemType === ItemTypeConsts.group) {
        url = `${this.route}/${urlType[item.type]}/${itemId}/details`;
      } else {
        url = `${this.route}/${itemId}/details`;
      }

      RouterService.redirect({
        path: url,
        viewport: this.viewport,
        vueRouter: this.$router,
      });
    },

    handleMainAction() {
      const { triggerEvent } = EventManager;
      triggerEvent(events.addNewGroupButtonClicked);

      if (this.itemType === ItemTypeConsts.configurationGroup
      && !!this.$hasFeatureFlag(this.$featureFlags.policyGroupTemplateGallery)
      && (this.isAdminWithBilling || this.isAdmin || this.isManager)) {
        return this.mainActionMenuItems;
      }
      return PageNavigationUtil.setLocation(`${this.route}/new`);
    },

    handleOpenNewConfigurationGroupClick() {
      PageNavigationUtil.setLocation(`${this.route}/new`);
    },

    handleOpenNewPolicyGroupTemplateClick() {
      this.$options.EventHelper.triggerAddPolicyGroupFromTemplateClicked({
        [description]: 'Save Policy Group From Template Clicked',
        [location]: policyGroupsPage,
        [section]: 'Add Menu',
        [text]: 'Save Policy Group From Template Menu Item',
      });
      PageNavigationUtil.setLocation(`${this.route}/grouptemplates`);
    },

    promptUndeletableGroupModal() {
      const { adGroups } = this.groupsIneligibleForDeletion;
      const groupNames = adGroups.map(adGroup => adGroup.name).join(', ');

      const headerText = `Unable to Delete ${pluralize('Group', adGroups.length)}`;
      const bodyText = `User Groups managed in Active Directory may not be deleted from the JumpCloud Directory. The following will not be deleted: ${groupNames}`;

      ModalEventBus.open({
        isLargeModal: false,
        component: 'ConfirmationModal',
        componentProps: {
          bodyText,
          cancelText: '',
          headerText,
        },
      });
    },

    showDeleteModal() {
      const selectedCount = this.selectedItems.length;

      if (!selectedCount) {
        return;
      }

      if (this.itemType === ItemTypeConsts.userGroup) {
        this.showDeleteUserGroupModal = true;
        this.severityModalData.kbText = 'Getting Started: User Groups.';
        this.severityModalData.kbLink = KbArticleUrls.gettingStartedGroups;
        this.severityModalData.notificationTitle = 'User Group Deletion';
        this.severityModalData.numConfirmText = 'Confirm the number of user groups to be deleted.';
        this.severityModalData.subtitle = 'Are you sure you want to delete this user group?';
        this.severityModalData.title = 'Delete User Group';
      } else {
        ModalEventBus.open({
          isLargeModal: false,
          component: 'DeleteConfirmationNumberModal',
          componentProps: {
            action: this.continueBulkDelete,
            itemType: this.getItemTableType,
          },
        });
      }
    },

    subtitleFunction(item) {
      if (item.type === systemGroup) {
        if (this.itemType === deviceGroup) {
          return `Group of ${pluralize(ItemTypeDisplayNames[device])}`;
        }

        return 'Group of Systems';
      } if (item.type === policyGroup) {
        return `Group of ${pluralize(ItemTypeDisplayNames[configuration])}`;
      }

      return 'Group of Users';
    },

    /**
     * Update data fields when displaying device groups or user groups.
     *
     * 1. Collection filters are passed to the API to limit the list of groups to either device,
     *    configuration or user groups, instead of all groups.
     * 2. The main action menu items are removed so that clicking the plus button navigates
     *    directly to the appropriate aside, rather than opening a menu to choose which type of
     *    group to create.
     * 3. The route is updated to point to either #/groups/device or #/groups/user.
     */
    updateData() {
      const groupsFilter = {
        [configurationGroup]: policyGroup,
        [deviceGroup]: systemGroup,
      };

      if (this.itemType !== ItemTypeConsts.group) {
        const value = groupsFilter[this.itemType] || userGroup;

        this.collectionFilters.push({
          name: 'type',
          operator: 'eq',
          value,
        });

        const route = get(this.$route, 'path', undefined);
        const filtersEnabled = LocalStorageService
          .getItem(LocalStorageConsts.membershipFiltersEnabled);
        if (filtersEnabled || (route && route === '/groups/user/membership/suggested/updates')) {
          LocalStorageService.removeItem(LocalStorageConsts.membershipFiltersEnabled);
          this.collectionFilters.push(Groupfilters.membershipSuggestion(true));
        }
      }

      this.mainActionMenuItems = [];
      this.route = ItemTypeRoutes[this.itemType];
    },
  },
};
</script>
